import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { BulkCreateAttendanceTimeBlockCompensationMutation, Maybe } from 'generated/resources/graphql'

type AttendanceManualCompensation = NonNullable<
  NonNullable<
    BulkCreateAttendanceTimeBlockCompensationMutation['attendanceMutations']['bulkCreateTimeBlockSupplementAttendanceManualCompensation']
  >['manualCompensation']
>

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import i18n from 'modules/core/lib/i18n'

export interface Props {
  response: AttendanceManualCompensation | undefined | null
  onCloseModal: () => void
}

const getSuccessDescription = (count: number) => {
  return i18n.tp('time_tracking.modals.time_blocks_compensation.success_description', { count })
}

const getErrorDescription = (count: number) => {
  return i18n.tp('time_tracking.modals.time_blocks_compensation.error_description', { count })
}

const CompensationErrorResponse = {
  ClosedPolicyPeriod: 'closed_policy_period',
  NoMapping: 'no_mapping',
  NoOvertime: 'no_overtime',
  NoPayrollPool: 'no_payroll_pool',
  NoPolicyPeriod: 'no_policy_period',
  NotAssignedToAnyPolicy: 'not_assigned_to_any_policy',
  SourceIdError: 'source_id_error',
  UnauthorizedToCreateSupplement: 'unauthorized_to_create_supplement',
}

const ResponseModal = ({ response, onCloseModal }: Props) => {
  if (!response) return <></>

  const errorResponse = (error: Maybe<string> | undefined) => {
    switch (error) {
      case CompensationErrorResponse.ClosedPolicyPeriod:
        return i18n.t('time_tracking.modals.time_blocks_compensation.errors.closed_policy_period')
      case CompensationErrorResponse.NoMapping:
        return i18n.t('time_tracking.modals.time_blocks_compensation.errors.no_mapping')
      case CompensationErrorResponse.NoOvertime:
        return i18n.t('time_tracking.modals.time_blocks_compensation.errors.no_overtime')
      case CompensationErrorResponse.NoPayrollPool:
        return i18n.t('time_tracking.modals.time_blocks_compensation.errors.no_payroll_pool')
      case CompensationErrorResponse.NoPolicyPeriod:
        return i18n.t('time_tracking.modals.time_blocks_compensation.errors.no_policy_period')
      case CompensationErrorResponse.NotAssignedToAnyPolicy:
        return i18n.t(
          'time_tracking.modals.time_blocks_compensation.errors.not_assigned_to_any_policy'
        )
      case CompensationErrorResponse.SourceIdError:
        return i18n.t('time_tracking.modals.time_blocks_compensation.errors.source_id_error')
      case CompensationErrorResponse.UnauthorizedToCreateSupplement:
        return i18n.t(
          'time_tracking.modals.time_blocks_compensation.errors.unauthorized_to_create_supplement'
        )

      default:
        return i18n.t('time_tracking.modals.time_blocks_compensation.errors.default_error')
    }
  }

  return (
    <Modal
      title={i18n.t(`time_tracking.modals.time_blocks_compensation.title`)}
      illustration="cash"
      footer={
        <Button onClick={onCloseModal}>
          {i18n.t('time_tracking.modals.compensation.payroll.response.ok')}
        </Button>
      }
    >
      <Box justifyContent="flexStart" alignItems="flexStart" gap="s40" width="full">
        {response.successes.length > 0 && (
          <Text.Normal>{getSuccessDescription(response.successes.length)}</Text.Normal>
        )}
        {response.errors.length > 0 && (
          <Box justifyContent="flexStart" alignItems="flexStart" gap="s12" width="full">
            <Text.Normal weight="medium">{getErrorDescription(response.errors.length)}</Text.Normal>
            {response.errors.map((error) => (
              <Box justifyContent="flexStart" alignItems="flexStart" gapDivider key={error.error}>
                <Text.Normal>{errorResponse(error.error)}</Text.Normal>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Modal>
  )
}

export default observer(ResponseModal)
